//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import 'video.js/dist/video-js.min.css';
import videojs from 'video.js';
// import RecordRTC from 'recordrtc';
import 'videojs-record/dist/css/videojs.record.css';
import Record from 'videojs-record/dist/videojs.record.js'; // do NOT remove this import or video.js record will throw Error: plugin "record" does not exist
import 'videojs-record/dist/plugins/videojs.record.ts-ebml.js';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { VIDEO_RESUME_MAX_LENGTH } from '../../system/constants';
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    PROFILE_VIDEO_OVERVIEW,
    VIDEO_OVERVIEW_MODE_VIEW_EXISTING,
    VIDEO_OVERVIEW_MODE_RECORD,
    VIDEO_OVERVIEW_MODE_FROM_LIBRARY,
    CONFIRM_DIALOG_TITLE_CANCEL,
    CONFIRM_DIALOG_MESSAGE_CANCEL,
    ERROR_MESSAGE_VIDEO_EXCEED_60_SECONDS,
    ERROR_MESSAGE_INVALID_VIDEO,
    CONFIRM_DIALOG_TITLE_DELETE_VIDEO,
    CONFIRM_DIALOG_MESSAGE_DELETE_VIDEO,
    VIDEO_OVERVIEW_MODE_FROM_CAMERA,
} from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { sendVideoOverview, deleteVideoOverview } from '../../common/services/myProfileServices';
// import { detectInappropriateVideo } from '../../common/sightEngineHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdLinearProgress from '../../common/elemental/AfitdLinearProgress';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import VideoOverviewBlock from './VideoOverviewBlock';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { open, mode, videoData, videoDuration } = params;

    const dialogHideStyles = {
        display: 'none',
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
    };

    const dialogShowStyles = {
        display: 'block',
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        '.displayBlock > .fade': {
            '-webkit-animation-name': 'fadeIn',
            'animation-name': 'fadeIn',
            '-webkit-animation-duration': '0.5s',
            'animation-duration': '0.5s',
            '-webkit-animation-fill-mode': 'both',
            'animation-fill-mode': 'both',
        },
    };

    const dialogStyles = open === true ? dialogShowStyles : dialogHideStyles;

    const videoOverviewContainerIsHidden = mode !== VIDEO_OVERVIEW_MODE_VIEW_EXISTING && mode !== VIDEO_OVERVIEW_MODE_FROM_LIBRARY && mode !== VIDEO_OVERVIEW_MODE_FROM_CAMERA;

    const videoRecordContainerIsHidden = mode !== VIDEO_OVERVIEW_MODE_RECORD;

    const errorMessageText1IsHidden = !videoData || videoDuration === Infinity || _isNil(videoDuration) || (videoDuration > 0 && videoDuration < VIDEO_RESUME_MAX_LENGTH + 1);

    const errorMessageText2IsHidden = !videoData || (videoDuration !== Infinity && !_isNil(videoDuration));

    return makeStyles(theme => ({
        dialog: {
            ...dialogStyles,
        },
        backdrop: {
            background: 'rgba(0, 0, 0, 0.6)',
            position: 'fixed',
            top: '0',
            left: '0',
            height: '100%',
            width: '100%',
        },
        dialogContentContainer: {
            position: 'fixed',
            background: 'white',
            // width: '86%',
            width: 'auto',
            height: 'auto',
            // maxWidth: '966px',
            // maxHeight: '540px',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            '-webkit-box-shadow': '0 10px 25px rgba(0,0,0,0.5)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
            borderRadius: '4px',
            border: '4px solid #ffff',
            padding: '20px 20px 20px 20px',
            [theme.breakpoints.down('sm')]: { width: '340px' },
        },
        titleContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: '600',
            fontSize: '20px',
            padding: '0 0 20px 0',
            [theme.breakpoints.down('sm')]: { padding: '0 0 10px 0' },
        },
        closeIconButton: {
            width: '20px',
            height: '20px',
        },
        dialogContent: {
            // overflowY: 'hidden', //'visible',
            '&::-webkit-scrollbar-track': {
                // '-webkit-box-shadow': 'inset 0 0 1px rgba(0,0,0,0.1)',
                borderRadius: '10px',
                background: 'none',
            },
            '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F5F5F5',
            },
            '&::-webkit-scrollbar-thumb': {
                // '-webkit-box-shadow': 'inset 0 0 1px rgba(0,0,0,0.1)',
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.2)',
            },
        },
        dialogContentLabelContainer: {
            display: 'flex',
            flexDirection: 'row',
            padding: '0 0 8px 0',
        },
        dialogContentLabel1: {
            fontFamily: 'Roboto, sans-serif',
            fontWeight: '600',
            fontSize: '14px',
        },
        dialogContentLabel2: {
            fontFamily: 'Roboto, sans-serif',
            fontWeight: '400',
            fontSize: '14px',
        },
        dialogContentBlock: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            borderRadius: '4px',
            // margin: '13px 0 0 0',
            // border: '1px solid #D5D7DD',
            padding: '30px 30px 30px 30px',

            background: '#FCFFFF',
            borderColor: `${theme.palette.secondary.main}`,
            border: '2px solid',

            '&:focus-within': {
                background: '#FCFFFF',
                borderColor: `${theme.palette.secondary.main}`,
                border: '2px solid',
            },
            [theme.breakpoints.down('sm')]: { padding: '15px' },
        },
        videoOverviewContainer: {
            // height: '360px',
            display: videoOverviewContainerIsHidden ? 'none' : undefined,
        },
        videoRecordContainer: {
            display: videoRecordContainerIsHidden ? 'none' : undefined,
        },
        errorMessageContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        },
        errorMessageText: { color: 'red' },
        actionContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            // width: '640px',
            padding: '16px 0 0 0',
            [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse', justifyContent: 'center' },
        },
        rightActionContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            flexGrow: '2',
            [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse', justifyContent: 'center', marginBottom: 10 },
        },
        deleteButton: {
            marginRight: '16px',
            [theme.breakpoints.down('sm')]: { width: '100%', marginRight: 0 },
        },
        cancelButton: {
            marginRight: '16px',
            [theme.breakpoints.down('sm')]: { width: '100%', marginRight: 0, marginTop: 10 },
        },
        recordButton: { marginBottom: 10, marginTop: 10 },
        errorMessageText1: { display: errorMessageText1IsHidden ? 'none' : undefined, color: 'red' },
        errorMessageText2: { display: errorMessageText2IsHidden ? 'none' : undefined, color: 'red' },
        hidden: {
            display: 'none',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        isDirty,
        setIsDirty,
        userId,
        cleanUpDialog,
        isConfirmCloseDialogOpen,
        setIsConfirmCloseDialogOpen,
        isConfirmDeleteDialogOpen,
        setIsConfirmDeleteDialogOpen,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        videoData,
        setVideoData,
        videoPlayerRef,
        // fileInputRef,
        mode,
        setMode,
        videoDuration,
        setVideoDuration,
        handleLoadVideoOverviewBlockMetadata,
        progress,
        setProgress,
        deleteLoading,
        setDeleteLoading,
        hideThumbnail,
        setHideThumbnail,
    } = params;
    // const { open } = props;

    const videoOverviewUrl = _get(props, 'videoOverviewUrl', '');
    const videoOverviewKey = _get(props, 'videoOverviewKey', '');
    const videoOverviewName = _get(props, 'videoOverviewName', '');
    const recordButtonDisabled = mode === VIDEO_OVERVIEW_MODE_RECORD || mode === VIDEO_OVERVIEW_MODE_FROM_CAMERA;
    const deleteButtonDisabled = mode !== VIDEO_OVERVIEW_MODE_VIEW_EXISTING;

    const selectedFileUrl = _get(videoData, 'url');
    const selectedFileType = _get(videoData, 'type');

    // disable Save button if one of the following is true:
    //   - Page is NOT dirty
    //   - Record mode and page is NOT dirty
    //   - From Library mode, and duration is null or longer than 60 seconds
    const saveButtonDisabled =
        !isDirty ||
        (mode === VIDEO_OVERVIEW_MODE_RECORD && !isDirty) ||
        ((mode === VIDEO_OVERVIEW_MODE_FROM_LIBRARY || mode === VIDEO_OVERVIEW_MODE_FROM_CAMERA) && (!videoDuration || videoDuration >= VIDEO_RESUME_MAX_LENGTH + 1));

    const defns = {
        dialog: {},
        backdrop: {
            onClick: () => {
                return true;
            },
        },
        videoOverviewContainer: {},
        videoOverviewBlock: {
            videoOverviewUrl:
                mode === VIDEO_OVERVIEW_MODE_VIEW_EXISTING
                    ? videoOverviewUrl
                    : mode === VIDEO_OVERVIEW_MODE_FROM_LIBRARY || mode === VIDEO_OVERVIEW_MODE_FROM_CAMERA
                    ? selectedFileUrl
                    : '',
            videoOverviewFileType: mode === VIDEO_OVERVIEW_MODE_FROM_LIBRARY || mode === VIDEO_OVERVIEW_MODE_FROM_CAMERA ? selectedFileType : undefined,
            onLoadedMetadata: handleLoadVideoOverviewBlockMetadata,
            onPlayVideo: () => setHideThumbnail(true),
            hideThumbnail,
        },
        videoRecordContainer: {},
        closeIconButton: {
            onClick: handleClickCancelButton.bind(null, isDirty, setIsConfirmCloseDialogOpen, cleanUpDialog),
        },
        deleteButton: {
            disabled: deleteButtonDisabled,
            loading: deleteLoading,
            onClick: handleClickDeleteButton.bind(null, setIsConfirmDeleteDialogOpen),
        },
        cancelButton: {
            onClick: handleClickCancelButton.bind(null, isDirty, setIsConfirmCloseDialogOpen, cleanUpDialog),
        },
        playButton: {
            onClick: handleClickPlayButton.bind(null, videoPlayerRef),
        },
        recordButton: {
            disabled: recordButtonDisabled,
            // onClick: handleClickRecordButton.bind(null, isDirty, setIsConfirmCloseDialogOpen, cleanUpDialog, videoPlayerRef),
            onClick: handleClickRecordButton.bind(null, setMode),
        },
        libraryButton: {
            // onClick: handleClickRecordButton.bind(null, isDirty, setIsConfirmCloseDialogOpen, cleanUpDialog, videoPlayerRef),
        },
        saveButton: {
            color: 'primary',
            disabled: saveButtonDisabled,
            onClick: handleClickSaveButton.bind(null, mode, videoData, videoDuration, cleanUpDialog, userId, setProgress, setSnackbarMessage, setSnackbarErrorMessage),
        },
        confirmCloseDialog: {
            open: isConfirmCloseDialogOpen,
            onClose: handleCloseConfirmCloseDialog.bind(null, setIsConfirmCloseDialogOpen),
        },
        confirmCloseDialogCancelButton: {
            onClick: handleCloseConfirmCloseDialog.bind(null, setIsConfirmCloseDialogOpen),
        },
        confirmCloseDialogOkButton: {
            autoFocus: true,
            onClick: handleClickConfirmCloseDialogYes.bind(null, cleanUpDialog, setIsConfirmCloseDialogOpen),
        },
        selectFileInput: {
            type: 'file',
            style: { display: 'none' },
            onChange: handleChangeFile.bind(null, setMode, setVideoData, setVideoDuration, setIsDirty),
            accept: 'video/*',
        },
        errorMessageText1: {
            variant: 'body2',
        },
        errorMessageText2: {
            variant: 'body2',
        },
        uploadLinearProgress: {
            variant: 'determinate',
            value: progress,
            isHidden: _isNil(progress),
        },
        confirmCancelDialog: {
            title: CONFIRM_DIALOG_TITLE_CANCEL,
            open: isConfirmCloseDialogOpen,
            message: CONFIRM_DIALOG_MESSAGE_CANCEL,
            onConfirm: handleClickConfirmCloseDialogYes.bind(null, cleanUpDialog, setIsConfirmCloseDialogOpen),
            onClose: handleCloseConfirmCloseDialog.bind(null, setIsConfirmCloseDialogOpen),
        },
        confirmDeleteDialog: {
            title: CONFIRM_DIALOG_TITLE_DELETE_VIDEO,
            open: isConfirmDeleteDialogOpen,
            message: CONFIRM_DIALOG_MESSAGE_DELETE_VIDEO,
            onConfirm: handleClickConfirmDeleteDialogYes.bind(
                null,
                userId,
                videoOverviewKey,
                videoOverviewName,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                cleanUpDialog,
                setIsConfirmDeleteDialogOpen,
                setDeleteLoading,
            ),
            onClose: handleCloseConfirmDeleteDialog.bind(null, setIsConfirmDeleteDialogOpen),
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleChangeFile = (setMode, setVideoData, setVideoDuration, setIsDirty, evt) => {
    // console.log('%c@@@@ handleChangeFile START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ handleChangeFile START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ handleChangeFile START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ handleChangeFile START', 'background: #222; color: #bada55');

    const file = _get(evt, 'target.files[0]');

    if (file) {
        setVideoDuration(null); //clear previous file duration
        const fileExtension = file.name.split('.').pop();

        const url = (window.URL || window.webkitURL).createObjectURL(file);
        setVideoData({ file, url, type: fileExtension });

        setMode(VIDEO_OVERVIEW_MODE_FROM_LIBRARY);
        setIsDirty(true);
    }
};

const handleCloseConfirmCloseDialog = setIsConfirmCloseDialogOpen => {
    setIsConfirmCloseDialogOpen(false);
};

const handleClickConfirmCloseDialogYes = (cleanUpDialog, setIsConfirmCloseDialogOpen) => {
    setIsConfirmCloseDialogOpen(false);
    cleanUpDialog();
};

const handleClickCancelButton = (isDirty, setIsConfirmCloseDialogOpen, cleanUpDialog, videoPlayerRef) => {
    if (isDirty) {
        setIsConfirmCloseDialogOpen(true);
    } else {
        cleanUpDialog();
    }
};

const handleClickRecordButton = setMode => {
    // const handleClickRecordButton = (isDirty, setIsConfirmCloseDialogOpen, cleanUpDialog, videoPlayerRef) => {
    console.log('%c@@@@ handleClickRecordButton START', 'background: #222; color: #bada55');
    console.log('%c@@@@ handleClickRecordButton START', 'background: #222; color: #bada55');
    console.log('%c@@@@ handleClickRecordButton START', 'background: #222; color: #bada55');

    setMode(VIDEO_OVERVIEW_MODE_RECORD);
    // // if(videoPlayerRef && videoPlayerRef.current && videoPlayerRef.current.parentElement && videoPlayerRef.current.parentElement.player && videoPlayerRef.current.parentElement.player.record) {
    // //     console.log('%c@@@@ handleClickRecordButton INSIDE IF', 'background: #222; color: #bada55');
    // //     videoPlayerRef.current.parentElement.player.record().getDevice();
    // // }

    // if (videojs && videojs('vjs_videoOverviewDialog')) {
    //     console.log('%c@@@@ handleClickRecordButton INSIDE IF videojs(vjs_videoOverviewDialog)=', 'background: #222; color: #bada55', videojs('vjs_videoOverviewDialog'));

    //     // videojs('vjs_videoOverviewDialog').player.record().getDevice();
    //     videojs('vjs_videoOverviewDialog').record().getDevice();
    // }
    // // if(videojs && videojs.players && videojs.players.vjs_videoOverviewDialog) {

    // //     console.log('%c@@@@ handleClickRecordButton INSIDE IF videojs.players.vjs_videoOverviewDialog=', 'background: #222; color: #bada55', videojs.players.vjs_videoOverviewDialog);

    // //     videojs.players.vjs_videoOverviewDialog.player.record().getDevice();
    // // }

    // // if (isDirty) {
    // //     setIsConfirmCloseDialogOpen(true);
    // // } else {
    // //     cleanUpDialog();
    // // }
};

const handleClickSaveButton = async (mode, videoData, videoDuration, cleanUpDialog, userId, setProgress, setSnackbarMessage, setSnackbarErrorMessage) => {
    // saveProfileSocialMedia({userId, facebookUrl, linkedInUrl, youTubeUrl, twitterUrl, websiteUrl, setSnackbarMessage, setSnackbarErrorMessage});

    // cleanUpDialog();

    // console.log('%c@@@@ handleClickSaveButton START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ handleClickSaveButton START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ handleClickSaveButton START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ handleClickSaveButton', 'background: #222; color: #bada55', { videoData });

    // if (mode === VIDEO_OVERVIEW_MODE_RECORD) {
    //     if (videoData) {
    //         console.log('%c@@@@ handleClickSaveButton videoData exists', 'background: #222; color: #bada55');

    //         sendVideoOverview({ mode, video: videoData, duration: 888, userId, setSnackbarMessage, setSnackbarErrorMessage, cleanUpDialog });
    //     }
    // } else if (mode === VIDEO_OVERVIEW_MODE_FROM_LIBRARY) {
    //     if (selectedFileData && selectedFileData.file) {
    //         console.log('%c@@@@ handleClickSaveButton selectedFileData and file exists', 'background: #222; color: #bada55');

    //         sendVideoOverview({ mode, video: selectedFileData.file, duration: selectedFileDuration, userId, setSnackbarMessage, setSnackbarErrorMessage, cleanUpDialog });
    //     }
    // }

    if (videoData && videoData.file) {
        // console.log('%c@@@@ handleClickSaveButton videoData and file exists', 'background: #222; color: #bada55');
        console.log('videoData.file', videoData?.file);
        // const result = await detectInappropriateVideo(videoData?.file);
        // console.log('result', result);

        await sendVideoOverview({ mode, video: videoData.file, duration: videoDuration, userId, setProgress, setSnackbarMessage, setSnackbarErrorMessage, cleanUpDialog });
    }
};

const handleClickDeleteButton = setIsConfirmDeleteDialogOpen => {
    // saveProfileSocialMedia({userId, facebookUrl, linkedInUrl, youTubeUrl, twitterUrl, websiteUrl, setSnackbarMessage, setSnackbarErrorMessage});

    // cleanUpDialog();

    console.log('%c@@@@ handleClickDeleteButton START', 'background: #222; color: #bada55');
    console.log('%c@@@@ handleClickDeleteButton START', 'background: #222; color: #bada55');
    console.log('%c@@@@ handleClickDeleteButton START', 'background: #222; color: #bada55');

    setIsConfirmDeleteDialogOpen(true);
};

const handleCloseConfirmDeleteDialog = setIsConfirmDeleteDialogOpen => {
    setIsConfirmDeleteDialogOpen(false);
};

const handleClickConfirmDeleteDialogYes = async (userId, key, name, setSnackbarMessage, setSnackbarErrorMessage, cleanUpDialog, setIsConfirmDeleteDialogOpen, setDeleteLoading) => {
    setIsConfirmDeleteDialogOpen(false);
    if (userId) {
        console.log('%c@@@@ handleClickDeleteButton userId exists', 'background: #222; color: #bada55');

        setDeleteLoading(true);
        await deleteVideoOverview({ userId, key, name, setSnackbarMessage, setSnackbarErrorMessage, cleanUpDialog });
        setDeleteLoading(false);
    }
};

const handleClickPlayButton = videoPlayerRef => {
    console.log('%c@@@@ handleClickPlayButton START', 'background: #222; color: #bada55');
    console.log('%c@@@@ handleClickPlayButton START', 'background: #222; color: #bada55');
    console.log('%c@@@@ handleClickPlayButton START', 'background: #222; color: #bada55');
    console.log('%c@@@@ handleClickPlayButton videojs=', 'background: #222; color: #bada55', videojs);

    if (videojs && videojs('vjs_videoOverviewDialog')) {
        console.log('%c@@@@ handleClickPlayButton INSIDE IF videojs(vjs_videoOverviewDialog)=', 'background: #222; color: #bada55', videojs('vjs_videoOverviewDialog'));

        // videojs('vjs_videoOverviewDialog').player.record().getDevice();
        videojs('vjs_videoOverviewDialog').play();
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const VideoOverviewDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const videoPlayerRef = useRef(null); // Instead of ID
    const fileInputRef = useRef();
    // const vid = useRef();
    const playerRef = useRef(null);
    // const videoSrc = "https://media.w3.org/2010/05/sintel/trailer_hd.mp4";

    const context = useContext(LayoutContext);
    // const userId = _get(context, 'userId');
    const { userId, setSnackbarMessage, setSnackbarErrorMessage } = context;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onClose, open, videoOverviewUrl } = props;
    const defaultMode = videoOverviewUrl ? VIDEO_OVERVIEW_MODE_VIEW_EXISTING : VIDEO_OVERVIEW_MODE_RECORD;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [isConfirmCloseDialogOpen, setIsConfirmCloseDialogOpen] = useState(false);
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
    const [isPlayerReady, setIsPlayerReady] = useState(false);
    // const [isRequestGetDevice, setIsRequestGetDevice] = useState(false);
    // const [isDeviceReady, setIsDeviceReady] = useState(false);
    const [videoData, setVideoData] = useState();
    const [mode, setMode] = useState(defaultMode);
    const [videoDuration, setVideoDuration] = useState();
    const [progress, setProgress] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [hideThumbnail, setHideThumbnail] = useState(false);
    // const [selectedFileUrl, setSelectedFileUrl] = useState();
    // let player = null;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const userAvatar = _get(props, 'userAvatar');

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const cleanUpDialog = useCallback(() => {
        setProgress(null); // clear upload progress bar
        setIsDirty(false); // reset dirty flag
        onClose(); // fire onClose event
    }, [onClose]);

    // const setVid = useCallback(
    //     video => {
    //         vid.current = video;
    //     },
    //     [vid],
    // );

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        // if (videoPlayerRef && videoPlayerRef.current) {
        //     const videoJSOptions = {
        //         //   autoplay: "muted",
        //         //   controls: true,
        //         //   userActions: { hotkeys: true },
        //         //   playbackRates: [0.5, 1, 1.5, 2]
        //         // video.js options
        //         controls: true,
        //         bigPlayButton: false,
        //         loop: false,
        //         fluid: false,
        //         // width: 480,
        //         // height: 360,
        //         aspectRatio: "4:3",
        //         controlBar: {
        //             volumePanel: false,
        //             fullscreenToggle: false,
        //             pictureInPictureToggle: false,
        //         },
        //         plugins: {
        //             // videojs-record plugin options
        //             record: {
        //                 image: false,
        //                 audio: true,
        //                 video: true,
        //                 maxLength: 40,
        //                 debug: true,
        //                 // autoMuteDevice: true,
        //                 // videoMimeType: 'video/webm;codecs=H264',
        //                 // videoMimeType: 'video/mp4',
        //                 // audioMimeType: 'audio/webm',
        //                 // convertEngine: 'ts-ebml',
        //             },
        //         },
        //     };

        //     player = videojs(videoPlayerRef.current, videoJSOptions, () => {
        //         // player.src(videoSrc);
        //         player.on('ended', () => {
        //             console.log('ended');
        //         });

        //         player.on('finishRecord', function() {
        //             // show save as dialog
        //             console.log('finishRecord called');
        //             player.record().saveAs({'video': 'videoOverview.mp4'});
        //         });

        //         player.on('deviceReady', function() {
        //             console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
        //             console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
        //             console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
        //             console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
        //         });

        //         // player.on('finishConvert', function() {
        //         //     // the convertedData object contains the converted data that
        //         //     // can be downloaded by the user, stored on server etc.
        //         //     console.log('finished converting: ', player.convertedData);
        //         //     player.record().saveAs({'video': 'videoOverview.webm'});
        //         // });

        //         console.log('Player Ready');
        //     });
        //     // player.record().getDevice();

        //     return () => {
        //         player.dispose();
        //     };
        // }

        return () => {};
    }, []);

    useEffect(() => {
        if (mode === VIDEO_OVERVIEW_MODE_RECORD) {
            setIsDirty(false);
        }
    }, [mode]);

    useEffect(() => {
        console.log('%c@@@@ VideoOverviewDialog useEffect[open]', 'background: #222; color: #bada55');
        console.log('%c@@@@ VideoOverviewDialog useEffect[open]', 'background: #222; color: #bada55');
        console.log('%c@@@@ VideoOverviewDialog useEffect[open]', 'background: #222; color: #bada55');
        console.log('%c@@@@ VideoOverviewDialog useEffect[open] videoOverviewUrl=', 'background: #222; color: #bada55', videoOverviewUrl);

        let player = playerRef.current;

        if (open === true) {
            console.log('%c@@@@ VideoOverviewDialog useEffect[open] TRUE', 'background: #222; color: #bada55');
            // if(player) {
            //     console.log('%c@@@@ VideoOverviewDialog useEffect[open] TRUE getDevice', 'background: #222; color: #bada55');
            //     console.log('%c@@@@ VideoOverviewDialog useEffect[open] TRUE getDevice player=', 'background: #222; color: #bada55', player);
            //     player.record().getDevice();
            // }

            if (videoPlayerRef && videoPlayerRef.current) {
                // const videoJSPlayOptions = {
                //     //   autoplay: "muted",
                //     //   controls: true,
                //     //   userActions: { hotkeys: true },
                //     //   playbackRates: [0.5, 1, 1.5, 2]
                //     // video.js options
                //     controls: true,
                //     bigPlayButton: true,
                //     loop: false,
                //     fluid: false,
                //     // width: 480,
                //     // height: 360,
                //     width: 720,
                //     height: 480,
                //     // aspectRatio: "4:3",
                //     controlBar: {
                //         volumePanel: false,
                //         fullscreenToggle: true,
                //         pictureInPictureToggle: false,
                //     },
                // };

                // if player has not yet been initialized, initial it now.
                if (!player) {
                    const videoJSRecordOptions = {
                        //   autoplay: "muted",
                        //   controls: true,
                        //   userActions: { hotkeys: true },
                        //   playbackRates: [0.5, 1, 1.5, 2]
                        // video.js options
                        controls: true,
                        bigPlayButton: true,
                        loop: false,
                        fluid: false,
                        width: 720,
                        height: 480,
                        // aspectRatio: "4:3",
                        controlBar: {
                            volumePanel: false,
                            fullscreenToggle: true,
                            pictureInPictureToggle: false,
                            deviceButton: false,
                        },
                        plugins: {
                            // videojs-record plugin options
                            record: {
                                image: false,
                                audio: true,
                                video: true,
                                maxLength: VIDEO_RESUME_MAX_LENGTH,
                                // maxLength: 2,
                                debug: true,
                                // autoMuteDevice: true,
                                // videoMimeType: 'video/webm;codecs=H264',
                                // videoMimeType: 'video/webm;codecs=vp8,opus',  // THIS WORKS in Chrome
                                videoMimeType: 'video/webm;codecs=vp8',
                                // videoMimeType: 'video/mp4',
                                // audioMimeType: 'audio/webm',
                                convertEngine: 'ts-ebml',
                            },
                        },
                    };

                    // let videoJSOptions = videoJSRecordOptions;

                    // if (videoOverviewUrl) {
                    //     videoJSOptions = videoJSPlayOptions;
                    // }

                    playerRef.current = videojs(videoPlayerRef.current, videoJSRecordOptions, () => {
                        setIsPlayerReady(true);
                        // player.src(videoSrc);
                        // if (player) {
                        //     player.on('ended', () => {
                        //         console.log('ended');
                        //     });

                        //     player.on('finishRecord', function () {
                        //         // show save as dialog
                        //         console.log('%c@@@@ finishRecord START', 'background: #222; color: #bada55');
                        //         console.log('%c@@@@ finishRecord START', 'background: #222; color: #bada55');
                        //         console.log('%c@@@@ finishRecord START', 'background: #222; color: #bada55');
                        //         console.log('%c@@@@ finishRecord START', 'background: #222; color: #bada55');
                        //         console.log('%c@@@@ finishRecord player.recordedData=', 'background: #222; color: #bada55', player.recordedData);
                        //         console.log('%c@@@@ finishRecord player.recordedData.video=', 'background: #222; color: #bada55', player.recordedData.video);
                        //         //player.record().saveAs({'video': 'videoOverview.webm'});
                        //         // setVid(player.recordedData);
                        //         setVideoData(player.recordedData);
                        //     });

                        //     player.on('deviceReady', function () {
                        //         console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
                        //         console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
                        //         console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
                        //         console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
                        //     });
                        // }

                        // player.on('finishConvert', function() {
                        //     // the convertedData object contains the converted data that
                        //     // can be downloaded by the user, stored on server etc.
                        //     console.log('finished converting: ', player.convertedData);
                        //     player.record().saveAs({'video': 'videoOverview.webm'});
                        // });

                        // if(videoOverviewUrl) {
                        //     console.log('%c@@@@ VideoOverviewDialog INSIDE IF videoOverviewUrl', 'background: #222; color: #bada55');
                        //     console.log('%c@@@@ VideoOverviewDialog INSIDE IF videoOverviewUrl', 'background: #222; color: #bada55');
                        //     console.log('%c@@@@ VideoOverviewDialog INSIDE IF videoOverviewUrl', 'background: #222; color: #bada55');
                        //     console.log('%c@@@@ VideoOverviewDialog INSIDE IF videoOverviewUrl=', 'background: #222; color: #bada55', videoOverviewUrl);
                        //     player.src({ src: videoOverviewUrl })
                        // };

                        console.log('Player Ready');
                    });
                }

                // // comment this out as this is never true because it is inside if (!player) -el 20210702
                // if (videoOverviewUrl && player) {
                //     console.log('%c@@@@ VideoOverviewDialog INSIDE IF videoOverviewUrl', 'background: #222; color: #bada55');
                //     console.log('%c@@@@ VideoOverviewDialog INSIDE IF videoOverviewUrl', 'background: #222; color: #bada55');
                //     console.log('%c@@@@ VideoOverviewDialog INSIDE IF videoOverviewUrl', 'background: #222; color: #bada55');
                //     console.log('%c@@@@ VideoOverviewDialog INSIDE IF videoOverviewUrl videojs.players=', 'background: #222; color: #bada55', videojs.players);
                //     console.log('%c@@@@ VideoOverviewDialog INSIDE IF videoOverviewUrl=', 'background: #222; color: #bada55', videoOverviewUrl);
                //     // player.record().load({url:videoOverviewUrl});

                //     player.src({ src: videoOverviewUrl });
                // }

                // Start the camera and microphone
                // player.record().getDevice();

                // return () => {
                //     // console.log('%c@@@@ VideoOverviewDialog useEffect[open] DISPOSE', 'background: #222; color: #bada55');
                //     // player.dispose();

                //     // Stop the camera and microphone
                //     if (player?.record) {
                //         console.log('%c@@@@ VideoOverviewDialog useEffect[open] STOP DEVICE', 'background: #222; color: #bada55');
                //         player.record().stopDevice();
                //     }
                // };
            }
        }

        return () => {
            // Stop the camera and microphone
            if (playerRef?.current?.record) {
                console.log('%c@@@@ VideoOverviewDialog useEffect[open] STOP DEVICE', 'background: #222; color: #bada55');
                playerRef.current.record().stopDevice();
            }

            // Dispose the player
            if (playerRef?.current?.dispose) {
                console.log('%c@@@@ VideoOverviewDialog useEffect[open] DISPOSE', 'background: #222; color: #bada55');
                playerRef.current.dispose();
            }
        };
    }, [open, playerRef, /*setVid,*/ videoOverviewUrl]);

    useEffect(() => {
        if (isPlayerReady) {
            const player = playerRef.current;
            player.on('ended', () => {
                console.log('ended');
            });

            player.on('finishRecord', () => {
                // show save as dialog
                console.log('%c@@@@ finishRecord START', 'background: #222; color: #bada55');
                console.log('%c@@@@ finishRecord START', 'background: #222; color: #bada55');
                console.log('%c@@@@ finishRecord player.recordedData=', 'background: #222; color: #bada55', player.recordedData);
                console.log('%c@@@@ finishRecord player.recordedData.video=', 'background: #222; color: #bada55', player.recordedData.video);
                //player.record().saveAs({'video': 'videoOverview.webm'});
                // setVideoData(player.recordedData);
                // setIsDirty(true);
            });

            player.on('finishConvert', function () {
                // the convertedData object contains the converted data that can be downloaded by the user, stored on server etc.
                console.log('%c@@@@ finishConvert START', 'background: #222; color: #bada55');
                console.log('%c@@@@ finishConvert START', 'background: #222; color: #bada55');
                console.log('%c@@@@ finishConvert player.recordedData=', 'background: #222; color: #bada55', player.convertedData);
                // player.record().saveAs({'video': 'videoOverview.webm'});
                // setVideoData(player.convertedData);

                const url = (window.URL || window.webkitURL).createObjectURL(player.convertedData);
                setVideoData({ file: player.convertedData, url, type: 'webm' });

                setMode(VIDEO_OVERVIEW_MODE_FROM_CAMERA);
                setIsDirty(true);
            });

            player.on('deviceReady', () => {
                console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
                console.log('%c@@@@ videojs-record deviceReady', 'background: #222; color: #bada55');
            });

            player.on('deviceError', () => {
                console.log('%c@@@@ videojs-record deviceError', 'background: #222; color: #bada55');
                console.log('%c@@@@ videojs-record deviceError', 'background: #222; color: #bada55');
            });

            if (videojs && videojs('vjs_videoOverviewDialog')) {
                console.log('%c@@@@ handleClickCancelButton INSIDE IF videojs(vjs_videoOverviewDialog)=', 'background: #222; color: #bada55', videojs('vjs_videoOverviewDialog'));

                // videojs('vjs_videoOverviewDialog').player.record().getDevice();
                videojs('vjs_videoOverviewDialog').record().getDevice();
            }
        }
    }, [isPlayerReady]);

    // useEffect(() => {
    //     console.log('%c@@@@ VideoOverviewDialog useEffect[video]', 'background: #222; color: #bada55');
    //     console.log('%c@@@@ VideoOverviewDialog useEffect[video] video=', 'background: #222; color: #bada55', vid.current);
    // }, [vid]);

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const handleLoadVideoOverviewBlockMetadata = useCallback(metadata => {
        const duration = _get(metadata, 'duration');
        if (duration) {
            setVideoDuration(duration);
        }
    }, []);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useStyles({ open, mode, videoData, videoDuration })();
    const defns = getDefns({
        classes,
        userId,
        props,
        isDirty,
        setIsDirty,
        cleanUpDialog,
        isConfirmCloseDialogOpen,
        setIsConfirmCloseDialogOpen,
        isConfirmDeleteDialogOpen,
        setIsConfirmDeleteDialogOpen,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        videoPlayerRef,
        fileInputRef,
        // video: vid.current,
        videoData,
        setVideoData,
        mode,
        setMode,
        videoDuration,
        setVideoDuration,
        handleLoadVideoOverviewBlockMetadata,
        progress,
        setProgress,
        deleteLoading,
        setDeleteLoading,
        hideThumbnail,
        setHideThumbnail,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.dialog}>
            <div {...defns.backdrop} />
            <section {...defns.dialogContentContainer}>
                <div {...defns.titleContainer}>
                    <div>{PROFILE_VIDEO_OVERVIEW}</div>
                    <div>
                        <IconButton {...defns.closeIconButton}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>
                <div {...defns.dialogContentLabelContainer}>
                    <div {...defns.dialogContentLabel1}>Record/Select Video&nbsp;</div>
                    <div {...defns.dialogContentLabel2}>(max {VIDEO_RESUME_MAX_LENGTH} sec)</div>
                </div>
                <div {...defns.dialogContent}>
                    <div {...defns.dialogContentBlock}>
                        <div {...defns.videoOverviewContainer}>
                            <VideoOverviewBlock {...defns.videoOverviewBlock} userAvatar={userAvatar} />
                        </div>
                        <div {...defns.videoRecordContainer}>
                            <video ref={videoPlayerRef} id="vjs_videoOverviewDialog" playsinline class="video-js vjs-default-skin vjs-default-skin"></video>
                        </div>
                        {/* <AfitdButton {...defns.playButton}>Play</AfitdButton> */}
                        <AfitdButton {...defns.recordButton}>Re-record</AfitdButton>
                        {/* <AfitdButton {...defns.libraryButton}>
                            <div>Library</div>
                            <input {...defns.selectFileInput} ref={fileInputRef} />
                        </AfitdButton> */}
                        <Button
                            component="label"
                            disableElevation
                            variant="contained"
                            style={{
                                // backgroundColor: 'transparent',
                                // padding: 0,
                                textTransform: 'none',
                                color: '#000',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                '&:hover': {
                                    backgroundColor: '#EBE9E9',
                                },
                            }}
                        >
                            Upload a Video
                            <input {...defns.selectFileInput} ref={fileInputRef} />
                        </Button>
                        <div {...defns.errorMessageContainer}>
                            <AfitdTypography {...defns.errorMessageText1}>{ERROR_MESSAGE_VIDEO_EXCEED_60_SECONDS}</AfitdTypography>
                            <AfitdTypography {...defns.errorMessageText2}>{ERROR_MESSAGE_INVALID_VIDEO}</AfitdTypography>
                        </div>
                        <AfitdLinearProgress {...defns.uploadLinearProgress} />
                    </div>
                </div>
                <div {...defns.actionContainer}>
                    <AfitdButton {...defns.deleteButton}>Delete</AfitdButton>
                    <div {...defns.rightActionContainer}>
                        <AfitdButton {...defns.cancelButton}>Cancel</AfitdButton>
                        <AfitdButton {...defns.saveButton}>
                            {progress ? (
                                <>
                                    <CircularProgress size={20} color="#ffffff" />
                                    &nbsp;&nbsp; Saving
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </AfitdButton>
                    </div>
                </div>
            </section>
            {false && <Record />}
            <AfitdConfirmDialog {...defns.confirmCancelDialog} />
            <AfitdConfirmDialog {...defns.confirmDeleteDialog} />
        </div>
    );
};

export default VideoOverviewDialog;
